import { type InferResponseType, unwrap } from "@nexxt/api-client";
import { filterQuery } from "@nexxt/app-kit";
import type { ListManufacturersInput } from "@nexxt/contracts/party";
import { partyQueries } from "@nexxt/party-ui";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api } from "../../api";

// Server state of the manufacturers (50-api/party.md §2.6): who makes the goods the SKU attributes name
// (FR-M4-07), by page and by name, with the supplier each works for.

const manufacturers = api.party.manufacturers;

type ListPage = InferResponseType<typeof manufacturers.$get, 200>;
export type Manufacturer = ListPage["items"][number];

/** The filters of the list input (`listManufacturersInput`), without the paging the query adds. */
export type ManufacturerFilters = Pick<ListManufacturersInput, "q" | "supplierOrgId"> & {
  archived?: boolean | undefined;
};

const PAGE_SIZE = 50;

const keys = {
  all: [...partyQueries.all, "manufacturers"] as const,
  lists: [...partyQueries.all, "manufacturers", "list"] as const,
};

export const manufacturerQueries = {
  ...keys,

  list: (filters: ManufacturerFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.lists, filters],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          manufacturers.$get(
            { query: { ...filterQuery(filters), page: String(pageParam), pageSize: String(PAGE_SIZE) } },
            { init: { signal } },
          ),
        ),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  /** One manufacturer, for the panel of one the loaded pages do not have: a new one, or a link (RUB-14). */
  detail: (manufacturerId: string) =>
    queryOptions({
      queryKey: [...keys.all, "detail", manufacturerId],
      queryFn: ({ signal }) =>
        unwrap(manufacturers[":manufacturerId"].$get({ param: { manufacturerId } }, { init: { signal } })),
    }),
};
